/**
 * The shareable release summary: plain text that pastes into Slack as is,
 * and the same content as a printable page for "Download PDF".
 *
 * Same layout as the /release-summary skill's output, so both read alike:
 * last 3 releases per app and platform, builds in testing, admin panel
 * changes, and the "don't mention yet" list.
 */
import dayjs from 'dayjs';
import {
  APP_LABELS, App, AppBuild, AppRelease, Deployment, PLATFORM_LABELS, Platform, UnreleasedFeature,
} from './types';

const RELEASES_PER_PLATFORM = 3;
const ADMIN_ITEMS = 3;

// Store dates stay in UTC, as the App Store shows them (4.7 is "Oct 10" there,
// though it was the evening of Oct 9 in the US).
const day = (iso: string | null, confirmed = true) =>
  iso ? `${confirmed ? '' : '~'}${dayjs(iso.slice(0, 10)).format('MMM D')}` : 'date unknown';

const sentences = (text: string) =>
  text
    .split('\n')
    .map((l) => l.replace(/^[-•*]\s*/, '').trim())
    .filter(Boolean)
    .map((l) => (/[.!?]$/.test(l) ? l : `${l}.`))
    .join(' ');

/** "feat(admin-users): export honors…" -> "Export honors…" for raw commit subjects. */
const plainCommit = (line: string) => {
  const text = line.replace(/^\w+(\([^)]*\))?!?:\s*/, '').trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const latestPerPlatform = (releases: AppRelease[], app: App, platform: Platform) =>
  releases
    .filter((r) => r.app === app && r.platform === platform && r.released_at)
    .sort((a, b) => dayjs(b.released_at).valueOf() - dayjs(a.released_at).valueOf())
    .slice(0, RELEASES_PER_PLATFORM);

function appSection(app: App, releases: AppRelease[]): string[] {
  const ios = latestPerPlatform(releases, app, 'ios');
  const android = latestPerPlatform(releases, app, 'android');
  if (!ios.length && !android.length) return [];

  const live = (platform: Platform) =>
    releases.find((r) => r.app === app && r.platform === platform && r.is_live);
  const current = [
    live('ios') && `iPhone ${live('ios')?.version}`,
    live('android') && `Android ${live('android')?.version}`,
  ].filter(Boolean).join(', ');
  const lines = [`**${APP_LABELS[app]} app: ${current}**`];

  // One line per version; iPhone and Android merge when both shipped it.
  const versions = Array.from(new Set([...ios, ...android].map((r) => r.version)));
  versions
    .sort((a, b) => b.localeCompare(a, undefined, { numeric: true }))
    .forEach((version) => {
      const onIos = ios.find((r) => r.version === version);
      const onAndroid = android.find((r) => r.version === version);
      const androidDay = onAndroid ? day(onAndroid.released_at, onAndroid.date_confirmed) : '';
      let when = `${androidDay}, Android`;
      if (onIos) when = onAndroid ? `iPhone ${day(onIos.released_at)}, Android ${androidDay}` : `${day(onIos.released_at)}, iPhone`;
      const release = onIos || onAndroid;
      const text = release ? sentences(release.summary) || sentences(release.store_notes) : '';
      lines.push(`• **${version} (${when}):** ${text || 'No summary yet.'}`);
    });
  return [...lines, ''];
}

export function buildSummary(
  releases: AppRelease[],
  builds: AppBuild[],
  adminDeploys: Deployment[],
  unreleased: UnreleasedFeature[],
): string {
  const lines = [
    `**App and admin panel updates (as of ${dayjs().format('MMM D, YYYY')})**`,
    '',
    '_All mobile features below are live in the stores; customers get them by updating the app. '
      + 'Dates are store release dates._',
    '',
    ...appSection('ease', releases),
    ...appSection('wavon', releases),
  ];

  // Only builds from main: a branch build can carry unreleased work.
  const testing = new Map<string, AppBuild>();
  builds
    .filter((b) => b.status === 'testing' && b.on_main)
    .forEach((b) => testing.set(`${b.app}-${b.platform}-${b.version}`, b));
  if (testing.size) {
    lines.push('**Coming soon: in testing, not yet available**');
    testing.forEach((b) =>
      lines.push(`• **${APP_LABELS[b.app]} ${b.version} (${PLATFORM_LABELS[b.platform]})**`));
    lines.push('');
  }

  const admin = adminDeploys.filter((d) => d.summary.trim()).slice(0, ADMIN_ITEMS);
  if (admin.length) {
    lines.push('**Admin panel: internal staff tools (not for customer emails)**');
    admin.forEach((d) => {
      const text = d.summary.split('\n').filter(Boolean).map(plainCommit);
      lines.push(`• **${dayjs(d.deployed_at).format('MMM D')}:** ${sentences(text.join('\n'))}`);
    });
    lines.push('');
  }

  const active = unreleased.filter((f) => f.is_active);
  if (active.length) {
    lines.push("**Please don't mention yet (not available to customers)**");
    active.forEach((f) => lines.push(`• ${f.title}`));
  }
  return lines.join('\n').trim();
}

const escape = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const inline = (s: string) =>
  escape(s)
    .replace(/\*\*(.+?)\*\*/g, '<b>$1</b>')
    .replace(/(^|[^\w])_(.+?)_(?!\w)/g, '$1<i>$2</i>');

/** Opens the summary as a print-ready page; the browser's "Save as PDF" does the rest. */
export function printSummary(summary: string): void {
  const out: string[] = [];
  let inList = false;
  summary.split('\n').forEach((line) => {
    if (line.startsWith('• ')) {
      if (!inList) out.push('<ul>');
      inList = true;
      out.push(`<li>${inline(line.slice(2))}</li>`);
      return;
    }
    if (inList) out.push('</ul>');
    inList = false;
    if (!line.trim()) return;
    const heading = /^\*\*.+\*\*$/.test(line);
    const tag = heading ? (out.length ? 'h2' : 'h1') : 'p';
    out.push(`<${tag}>${inline(heading ? line.slice(2, -2) : line)}</${tag}>`);
  });
  if (inList) out.push('</ul>');

  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(`<!doctype html><html><head><meta charset="utf-8">
<title>Release summary ${dayjs().format('YYYY-MM-DD')}</title>
<style>
  body{font-family:-apple-system,Helvetica,sans-serif;max-width:680px;margin:24px auto;color:#222;line-height:1.4;font-size:13px}
  h1{font-size:20px;border-bottom:2px solid #2a6;padding-bottom:6px}
  h2{font-size:14.5px;margin:16px 0 6px;color:#2a6;break-after:avoid}
  ul{margin:4px 0}li{margin:3px 0;break-inside:avoid}p{margin:4px 0}p i{color:#666;font-size:12px}
  @page{margin:16mm}
</style></head><body>${out.join('')}</body></html>`);
  win.document.close();
  win.focus();
  win.print();
}
